import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Model as AdvicesModel } from '../../advices/index';
import { Model } from '../interfaces';
import * as actions from '../state/actions';
import * as selectors from '../state/selectors';

@Component({
  selector: 'app-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss'],
})
export class ReportsComponent implements OnInit, OnDestroy {
  constructor(private readonly store: Store) {}
  private readonly destroy$ = new Subject();
  public properties: AdvicesModel.AvailableProperty[];
  public reportDownloadError$: Observable<string>;
  public reportQuery: Model.FinancialReportQuery;
  public selectedProperties: number[];
  public selectedPropertiesList: string;
  public submitReady: boolean;

  // Bobby Calves Report properties
  public supplyYears: string[] = [];
  public selectedSupplyYear: string | null = null;
  public selectedBobbyCalvesProperties: number[] = [];
  public selectedBobbyCalvesPropertiesList: string = 'All Properties';
  public bobbyCalvesSubmitReady: boolean = true;
  public bobbyCalvesDownloadError$: Observable<string>;

  ngOnInit(): void {
    this.store.dispatch(actions.getFinancialReportOptions());
    this.store.dispatch(actions.getBobbyCalvesReportOptions());

    this.store
      .select(selectors.selectAvailableProperties)
      .pipe(takeUntil(this.destroy$))
      .subscribe((properties) => {
        this.properties = properties;

        // Default Bobby Calves property filter to all selected, like Financial Report.
        if (this.selectedBobbyCalvesProperties.length === 0 && properties.length > 0) {
          this.selectedBobbyCalvesProperties = [
            0,
            ...properties.map((property) => property.propertyId),
          ];
          this.updateBobbyCalvesPropertiesList();
        }
      });

    this.store
      .select(selectors.selectReportQuery)
      .pipe(takeUntil(this.destroy$))
      .subscribe((query) => (this.reportQuery = query));

    this.store
      .select(selectors.selectSubmitValid)
      .pipe(takeUntil(this.destroy$))
      .subscribe((ready) => (this.submitReady = ready));

    this.store
      .select(selectors.selectSelectedPropertiesList)
      .pipe(takeUntil(this.destroy$))
      .subscribe((list) => (this.selectedPropertiesList = list));

    this.store
      .select(selectors.selectSelectedProperties)
      .pipe(takeUntil(this.destroy$))
      .subscribe((properties) => (this.selectedProperties = properties));

    this.store
      .select(selectors.selectAvailableSupplyYears)
      .pipe(takeUntil(this.destroy$))
      .subscribe((supplyYears) => {
        this.supplyYears = supplyYears;
        this.selectedSupplyYear = supplyYears.length > 0 ? supplyYears[0] : null;
      });

    this.reportDownloadError$ = this.store.select(
      selectors.selectFinancialReportDownloadError
    );

    // Bobby Calves Report initialization
    this.supplyYears = [];
    this.selectedSupplyYear = null;
    this.selectedBobbyCalvesProperties = [];
    this.selectedBobbyCalvesPropertiesList = 'All Properties';
    this.bobbyCalvesSubmitReady = true;
    this.bobbyCalvesDownloadError$ = this.store.select(
      selectors.selectBobbyCalvesReportDownloadError
    );
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public download(): void {
    if (this.submitReady) {
      this.store.dispatch(actions.downloadFinancialReport(this.reportQuery));
    }
  }

  public clearFilter(): void {
    this.store.dispatch(actions.clearFilter());
  }

  public updateFilter(update: Partial<Model.FinancialReportQuery>): void {
    this.store.dispatch(actions.filterUpdated({ update }));
  }

  public toggleProperty(propertyId: number) {
    this.store.dispatch(actions.toggleFilterProperty({ propertyId }));
  }

  public toggleAllProperties(): void {
    this.store.dispatch(actions.toggleFilterAllProperties());
  }

  // Bobby Calves Report methods
  public downloadBobbyCalves(): void {
    if (!this.bobbyCalvesSubmitReady) return;

    const selectedPropertyIds = this.getSelectedBobbyCalvesPropertyIds();

    this.store.dispatch(
      actions.downloadBobbyCalvesReport({
        supplyYear: this.selectedSupplyYear,
        propertyIds: selectedPropertyIds,
      })
    );
  }

  public clearBobbyCalvesFilter(): void {
    this.selectedSupplyYear = this.supplyYears.length > 0 ? this.supplyYears[0] : null;
    this.selectedBobbyCalvesProperties = [
      0,
      ...this.properties.map((property) => property.propertyId),
    ];
    this.updateBobbyCalvesPropertiesList();
  }

  public updateBobbyCalvesProperties(selectedPropertyIds: number[]): void {
    const allPropertyIds = this.properties.map((property) => property.propertyId);
    const previouslyIncludedSelectAll = this.selectedBobbyCalvesProperties.includes(0);
    const includesSelectAll = selectedPropertyIds.includes(0);
    const selectedWithoutSelectAll = selectedPropertyIds.filter(
      (propertyId) => propertyId !== 0
    );

    const toggledSelectAllOn = includesSelectAll && !previouslyIncludedSelectAll;
    const toggledSelectAllOff =
      !includesSelectAll &&
      previouslyIncludedSelectAll &&
      selectedWithoutSelectAll.length === allPropertyIds.length;

    if (toggledSelectAllOn) {
      this.selectedBobbyCalvesProperties = [0, ...allPropertyIds];
    } else if (toggledSelectAllOff) {
      this.selectedBobbyCalvesProperties = [];
    } else if (selectedWithoutSelectAll.length === allPropertyIds.length) {
      this.selectedBobbyCalvesProperties = [0, ...allPropertyIds];
    } else {
      this.selectedBobbyCalvesProperties = selectedWithoutSelectAll;
    }

    this.updateBobbyCalvesPropertiesList();
  }

  private updateBobbyCalvesPropertiesList(): void {
    const selectedPropertyIds = this.selectedBobbyCalvesProperties.filter(
      (propertyId) => propertyId !== 0
    );

    if (selectedPropertyIds.length === 0) {
      this.selectedBobbyCalvesPropertiesList = 'All Properties';
    } else if (selectedPropertyIds.length === this.properties.length) {
      this.selectedBobbyCalvesPropertiesList = 'All Properties';
    } else {
      const selectedNames = this.properties
        .filter((property) => selectedPropertyIds.includes(property.propertyId))
        .map((property) => property.propertyName)
        .join(', ');
      this.selectedBobbyCalvesPropertiesList = selectedNames;
    }
  }

  private getSelectedBobbyCalvesPropertyIds(): number[] {
    const selectedPropertyIds = this.selectedBobbyCalvesProperties.filter(
      (propertyId) => propertyId !== 0
    );

    if (
      selectedPropertyIds.length === 0 ||
      selectedPropertyIds.length === this.properties.length
    ) {
      return this.properties.map((property) => property.propertyId);
    }

    return selectedPropertyIds;
  }
}
